/**
 * Mobile Speaker Sheet
 *
 * Mobile-accessible dialog for viewing and editing conversation settings
 * for a reflection. Provides a bottom-sheet style interface for:
 * - Therapy lens (style) selection with change confirmation
 * - Speaker label editing
 * - Speaker confirmation/identification
 * - Link to account settings
 *
 * This component is designed to be shown on mobile devices where the
 * ConversationSettingsPanel (desktop sidebar) is hidden.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { usePrimaryPartner } from "../../hooks/useConversationPartners";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useTherapyStyles } from "../../hooks/useTherapyStyles";
import {
  Loader2,
  User,
  Settings,
  Check,
  AlertCircle,
  Eye,
  Sparkles,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import type { SpeakerResponse } from "../../types/transcript";

interface MobileSpeakerSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when sheet is closed */
  onOpenChange: (open: boolean) => void;
  /** Speakers from the transcript */
  speakers: SpeakerResponse[];
  /** Whether speakers are bound/confirmed */
  speakersConfirmed: boolean;
  /** Callback to update speaker name */
  onUpdateSpeakerName?: (speakerId: string, name: string) => Promise<void>;
  /** Callback to confirm speaker binding */
  onConfirmBinding?: () => void;
  /** Whether updates are in progress */
  isUpdating?: boolean;
  /** Whether the reflection is with the primary partner */
  isWithPrimaryPartner?: boolean;
  /** Partner ID if specified */
  conversationPartnerId?: string | null;
  /** Currently selected therapy style ID */
  therapyStyleId?: string | null;
  /** Callback when therapy style changes */
  onTherapyStyleChange?: (styleId: string) => void;
}

export function MobileSpeakerSheet({
  open,
  onOpenChange,
  speakers,
  speakersConfirmed,
  onUpdateSpeakerName,
  onConfirmBinding,
  isUpdating = false,
  isWithPrimaryPartner = true,
  therapyStyleId,
  onTherapyStyleChange,
}: MobileSpeakerSheetProps) {
  const { data: user } = useCurrentUser();
  const { data: primaryPartner } = usePrimaryPartner();
  const { data: therapyStyles, isLoading: stylesLoading } = useTherapyStyles();

  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [showTherapyOptions, setShowTherapyOptions] = useState(false);
  const [pendingStyleChange, setPendingStyleChange] = useState<string | null>(
    null
  );

  const selectedStyle = therapyStyles?.find((s) => s.id === therapyStyleId);
  const pendingStyle = therapyStyles?.find((s) => s.id === pendingStyleChange);

  // Initialize editing names from speakers
  useEffect(() => {
    const names: Record<string, string> = {};
    speakers.forEach((speaker) => {
      names[speaker.id] = speaker.custom_name || speaker.display_label;
    });
    setEditingNames(names);
  }, [speakers]);

  const handleNameChange = (speakerId: string, name: string) => {
    setEditingNames((prev) => ({ ...prev, [speakerId]: name }));
  };

  const handleSaveName = async (speakerId: string) => {
    if (!onUpdateSpeakerName) return;

    const newName = editingNames[speakerId];
    const speaker = speakers.find((s) => s.id === speakerId);
    if (!speaker || newName === speaker.custom_name) return;

    try {
      await onUpdateSpeakerName(speakerId, newName);
      setSaveSuccess(speakerId);
      setTimeout(() => setSaveSuccess(null), 2000);
    } catch {
      // Error handled by parent
    }
  };

  const handleStyleSelect = (styleId: string) => {
    setShowTherapyOptions(false);
    if (styleId === therapyStyleId) return;

    if (therapyStyleId) {
      // Show confirmation for style change
      setPendingStyleChange(styleId);
    } else {
      onTherapyStyleChange?.(styleId);
    }
  };

  const handleConfirmStyleChange = () => {
    if (pendingStyleChange) {
      onTherapyStyleChange?.(pendingStyleChange);
    }
    setPendingStyleChange(null);
  };

  const getPartnerInfo = () => {
    if (isWithPrimaryPartner && primaryPartner) {
      return {
        name: primaryPartner.name,
        pronouns: primaryPartner.pronouns,
      };
    }
    // For now, fall back to user's legacy partner_name
    return {
      name: user?.partner_name || "Partner",
      pronouns: user?.partner_pronouns,
    };
  };

  const partnerInfo = getPartnerInfo();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Conversation Settings
          </DialogTitle>
          <DialogDescription>
            Adjust therapy lens and speaker settings for this recording.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Therapy Lens Section */}
          {onTherapyStyleChange && (
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <Eye className="w-3 h-3" />
                Therapy Lens
              </div>

              <button
                onClick={() => setShowTherapyOptions(!showTherapyOptions)}
                disabled={stylesLoading}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-muted/50 hover:bg-muted border border-border rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                  <span className="font-medium text-foreground truncate">
                    {selectedStyle?.name || "Select therapy style"}
                  </span>
                </div>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-muted-foreground transition-transform flex-shrink-0 ${showTherapyOptions ? "rotate-180" : ""}`}
                />
              </button>

              {showTherapyOptions && (
                <div className="rounded-lg border border-border bg-background overflow-hidden">
                  <div className="max-h-48 overflow-y-auto py-1">
                    {therapyStyles?.map((style) => {
                      const isSelected = therapyStyleId === style.id;
                      return (
                        <button
                          key={style.id}
                          onClick={() => handleStyleSelect(style.id)}
                          className={`w-full text-left px-3 py-2.5 hover:bg-muted transition-colors flex items-start gap-2 ${isSelected ? "bg-primary/5" : ""}`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {isSelected ? (
                              <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-primary-foreground" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-sm font-medium ${isSelected ? "text-primary" : "text-foreground"}`}
                            >
                              {style.name}
                            </div>
                            {style.description && (
                              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                {style.description}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Style change confirmation */}
              {pendingStyleChange && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        Change therapy lens?
                      </p>
                      <p className="mt-1 text-amber-700 dark:text-amber-300 text-xs">
                        This will regenerate all analysis and feedback. Existing
                        insights will be replaced.
                      </p>
                    </div>
                  </div>
                  <div className="text-xs space-y-1 bg-card/60 rounded p-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Current:</span>
                      <span className="font-medium">
                        {selectedStyle?.name || "None"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New:</span>
                      <span className="font-medium text-primary">
                        {pendingStyle?.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingStyleChange(null)}
                    >
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleConfirmStyleChange}>
                      Change Lens
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Speaker binding status */}
          {!speakersConfirmed && speakers.length > 0 && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-50 p-3 dark:bg-amber-950/20">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Speakers not confirmed
                  </p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    Review the speaker labels below and confirm they are
                    correct.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Participants section */}
          <div>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">
              Participants
            </h3>
            <div className="space-y-3">
              {/* User (self) */}
              <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {user?.first_name || "You"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.pronouns || "You"}
                  </p>
                </div>
              </div>

              {/* Partner */}
              <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
                  <User className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {partnerInfo.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {partnerInfo.pronouns || "Partner"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Speaker labels */}
          {speakers.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                Speaker Labels
              </h3>
              <div className="space-y-3">
                {speakers.map((speaker) => (
                  <div key={speaker.id} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-sm text-muted-foreground">
                        {speaker.external_label}
                      </label>
                      {speaker.canonical_label && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {speaker.canonical_label === "partner_a"
                            ? user?.first_name || "You"
                            : speaker.canonical_label === "partner_b"
                              ? partnerInfo.name
                              : speaker.canonical_label}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        value={editingNames[speaker.id] || ""}
                        onChange={(e) =>
                          handleNameChange(speaker.id, e.target.value)
                        }
                        placeholder="Speaker name"
                        disabled={isUpdating}
                        className="flex-1"
                      />
                      {onUpdateSpeakerName && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSaveName(speaker.id)}
                          disabled={
                            isUpdating ||
                            editingNames[speaker.id] ===
                              (speaker.custom_name || speaker.display_label)
                          }
                        >
                          {saveSuccess === speaker.id ? (
                            <Check className="h-4 w-4" />
                          ) : isUpdating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Save"
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link to account settings */}
          <div className="rounded-lg border border-dashed p-3">
            <p className="text-sm text-muted-foreground">
              Need to update your partner information or add more conversation
              partners?
            </p>
            <Button variant="link" asChild className="mt-1 h-auto p-0">
              <Link
                to="/account?tab=account"
                onClick={() => onOpenChange(false)}
              >
                <Settings className="mr-1 h-4 w-4" />
                Go to Account Settings
              </Link>
            </Button>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {!speakersConfirmed && onConfirmBinding && speakers.length > 0 && (
            <Button onClick={onConfirmBinding} disabled={isUpdating}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm Speakers
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {speakersConfirmed ? "Close" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
